import React, { useContext, useEffect } from 'react'
import { LinkContainer } from 'react-router-bootstrap'
import { Table, Button, Container, Row, Col } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Message from '../components/Message'
import Loader from '../components/Loader'
import { listUsers, deleteUser } from '../actions/userActions'
import moment from 'moment'

// Import icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  faCheckCircle,
  faEdit,
  faTimesCircle,
  faTrashCan
} from '@fortawesome/free-regular-svg-icons'
import { ThemeContext } from '../components/ThemeContext.js'
import {
  EMPLOYEE_PERMISSIONS,
  EMPLOYEE_PERMISSION_OPTIONS,
  hasEmployeePermission
} from '../constants/employeePermissions.js'

const UserListScreen = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { theme } = useContext(ThemeContext)

  const userList = useSelector((state) => state.userList)
  const { loading, error, users } = userList

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const canManageEmployees = Boolean(ownerInfo?.isOwner)
  const canResetEmployeePasswords = hasEmployeePermission(
    userInfo,
    ownerInfo,
    EMPLOYEE_PERMISSIONS.EMPLOYEE_PASSWORD_RESET
  )
  const canViewEmployeeList = canManageEmployees || canResetEmployeePasswords

  const userDelete = useSelector((state) => state.userDelete)
  const { successdelete } = userDelete

  const formatDate = (date) => {
    return moment(date).format('MM/DD/YYYY HH:mm')
  }

  const deleteHandler = (id) => {
    if (window.confirm('Are you sure')) {
      dispatch(deleteUser(id))
    }
  }

  useEffect(() => {
    if (!userInfo && !ownerInfo) {
      dispatch({ type: 'USER_LIST_RESET' })
      navigate('/login')
    } else if (!canViewEmployeeList) {
      dispatch({ type: 'USER_LIST_RESET' })
      navigate('/pos')
    } else {
      dispatch(listUsers())
    }
  }, [
    dispatch,
    navigate,
    userInfo,
    ownerInfo,
    canViewEmployeeList,
    successdelete
  ])

  return (
    <Container style={{ color: theme.text }}>
      <Row>
        <Col>
          <h3 className='my-2'>Users</h3>
        </Col>
        {canManageEmployees && (
          <Col className='text-right'>
            <Button
              className='my-2 btn-sm btn-outline-primary'
              size='sm'
              onClick={() => navigate('/admin/user/register')}
            >
              Create User
            </Button>
          </Col>
        )}
      </Row>

      {loading ?
        <Loader />
      : error ?
        <Message variant='danger'>{error}</Message>
      : <Table striped bordered hover responsive className='table-sm'>
          <thead>
            <tr>
              {canManageEmployees && (
                <>
                  <th>ID</th>
                  <th>LAST</th>
                </>
              )}
              <th>NAME</th>
              <th>EMAIL</th>
              {canManageEmployees ?
                <>
                  <th>PERMISSIONS</th>
                  <th>DISABLED</th>
                  <th></th>
                  <th></th>
                </>
              : <th>ACCESS</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id}>
                {canManageEmployees && (
                  <>
                    <td>{user._id}</td>
                    <td>{formatDate(user.lastLogin)}</td>
                  </>
                )}
                <td>{user.name}</td>
                <td>
                  <a
                    href={`mailto:${user.email}`}
                    style={{ color: theme.sidebarAccent }}
                  >
                    {user.email}
                  </a>
                </td>
                {canManageEmployees ?
                  <>
                    <td>
                      {(user.permissions || []).length > 0 ?
                        (user.permissions || [])
                          .map(
                            (permission) =>
                              EMPLOYEE_PERMISSION_OPTIONS.find(
                                (option) => option.value === permission
                              )?.label
                          )
                          .filter(Boolean)
                          .join(', ')
                      : 'Standard employee'}
                    </td>
                    <td>
                      {user.isDisabled ?
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          style={{ color: theme.success }}
                        />
                      : <FontAwesomeIcon
                          icon={faTimesCircle}
                          style={{ color: theme.danger }}
                        />
                      }
                    </td>
                    <td>
                      <LinkContainer to={`/admin/user/${user._id}`}>
                        <Button variant='primary' className='btn-sm'>
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      </LinkContainer>
                    </td>
                    <td>
                      <Button
                        variant='danger'
                        className='btn-sm'
                        onClick={() => deleteHandler(user._id)}
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </Button>
                    </td>
                  </>
                : <td>
                    <Button
                      variant='outline-primary'
                      size='sm'
                      onClick={() =>
                        navigate(`/admin/user/${user._id}/password`)
                      }
                    >
                      Reset password
                    </Button>
                  </td>
                }
              </tr>
            ))}
          </tbody>
        </Table>
      }
    </Container>
  )
}

export default UserListScreen
